import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import RegistrationProgress from '../../components/ui/RegistrationProgress';
import BasicInfoForm from './components/BasicInfoForm';
import ServicesForm from './components/ServicesForm';
import PortfolioForm from './components/PortfolioForm';
import AvailabilityForm from './components/AvailabilityForm';
import VerificationForm from './components/VerificationForm';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import vendorDataService from '../../services/vendorDataService';
import { getMainCategory } from '../../constants/categoryMapping';

const VendorRegistration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);

  // Auto-save functionality
  useEffect(() => {
    const savedData = localStorage.getItem('vendorRegistrationData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed.formData || {});
        setCurrentStep(parsed.currentStep || 1);
        setCompletedSteps(parsed.completedSteps || []);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Check for pending submission after user logs in
  useEffect(() => {
    const checkPendingSubmit = async () => {
      if (!user) return;

      const pendingData = localStorage.getItem('vendorRegistrationPendingSubmit');
      if (pendingData) {
        try {
          const parsed = JSON.parse(pendingData);
          if (parsed.readyToSubmit) {
            // Restore form data
            setFormData(parsed.formData);
            setCurrentStep(5); // Go to final step
            setCompletedSteps(parsed.completedSteps);

            // Auto-submit after a moment
            setTimeout(() => {
              handleSubmit();
            }, 1000);

            // Clear pending flag
            localStorage.removeItem('vendorRegistrationPendingSubmit');
          }
        } catch (error) {
          console.error('Error processing pending submission:', error);
        }
      }
    };

    checkPendingSubmit();
  }, [user]);

  // Save data to localStorage whenever formData changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem('vendorRegistrationData', JSON.stringify({
        formData,
        currentStep,
        completedSteps,
        lastSaved: new Date().toISOString()
      }));
      setHasUnsavedChanges(true);
    }
  }, [formData, currentStep, completedSteps]);

  const handleFormUpdate = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const handleStepClick = (stepId) => {
    if (completedSteps.includes(stepId) || stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    // Simulate save operation
    setTimeout(() => {
      setHasUnsavedChanges(false);
      setIsLoading(false);
    }, 1000);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      // Mark current step as completed
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(currentStep + 1);
      setHasUnsavedChanges(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // If user is not logged in, save to localStorage and redirect to sign in
      if (!user) {
        localStorage.setItem('vendorRegistrationPendingSubmit', JSON.stringify({
          formData,
          currentStep,
          completedSteps,
          readyToSubmit: true,
          timestamp: new Date().toISOString()
        }));

        // Show modal asking user to sign in/up to complete
        setError('Please sign in or create an account to complete your vendor registration');
        setTimeout(() => {
          navigate('/sign-in-page?redirect=/vendor-registration&action=complete');
        }, 2000);
        return;
      }
      // Convert subcategory to main database category
      const mainCategory = getMainCategory(formData.category);

      // Prepare vendor profile data for database
      const vendorData = {
        user_id: user.id,
        business_name: formData.businessName,
        category: mainCategory,
        description: formData.description || '',
        city: formData.city,
        state: formData.state,
        business_address: formData.address,
        phone: formData.phone,
        email: formData.email || user.email,
        website: formData.website || null,

        // Services and pricing
        services: formData.services || [],
        starting_price: parseFloat(formData.startingPrice) || 0,
        price_range: formData.priceRange || 'negotiable',

        // Cultural specializations
        cultural_specialties: formData.culturalSpecialties || [],
        languages: formData.languages || ['English'],

        // Availability
        years_experience: parseInt(formData.yearsExperience) || 0,
        team_size: parseInt(formData.teamSize) || 1,

        // Verification status
        is_verified: false,
        is_featured: false,
        subscription_type: 'free',

        // Metadata
        business_hours: formData.businessHours || {},
        social_media: {
          instagram: formData.instagram || null,
          facebook: formData.facebook || null,
          youtube: formData.youtube || null
        }
      };

      // Create vendor profile in database
      const result = await vendorDataService.createVendor(vendorData);

      if (!result.success) {
        throw new Error(result.error || 'Failed to create vendor profile');
      }

      // If portfolio images were uploaded, add them to vendor_gallery
      if (formData.portfolioImages && formData.portfolioImages.length > 0) {
        // In production, this would upload images to storage and create gallery entries
        console.log('Portfolio images to upload:', formData.portfolioImages);
      }

      // Mark final step as completed
      setCompletedSteps(prev => [...prev, 5]);

      // Clear saved data
      localStorage.removeItem('vendorRegistrationData');

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting vendor registration:', error);
      setError(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Navigate to vendor dashboard instead of profile
    navigate('/vendor-dashboard');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <ServicesForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <PortfolioForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <AvailabilityForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <VerificationForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <Header />

      {/* Progress Header */}
      <RegistrationProgress
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
        onSave={handleSave}
        onNext={handleNext}
        onPrevious={handlePrevious}
        isLoading={isLoading}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} color="#dc2626" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 mb-1">Registration Error</h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <Icon name="X" size={20} color="currentColor" />
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
          <div className="p-6 sm:p-8 lg:p-12">
            {renderCurrentStep()}
          </div>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-strong max-w-md w-full p-8 text-center animate-slide-up">
            <div className="flex items-center justify-center w-16 h-16 bg-success-50 rounded-full mx-auto mb-6">
              <Icon name="CheckCircle" size={32} color="#228B22" />
            </div>

            <h2 className="font-heading font-bold text-2xl text-text-primary mb-4">
              Registration Complete!
            </h2>

            <p className="text-text-secondary font-caption mb-6">
              Congratulations! Your vendor profile has been submitted for review.
              You'll receive an email confirmation shortly, and our team will verify
              your documents within 2-3 business days.
            </p>

            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSuccessClose}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={18}
                fullWidth
              >
                View My Profile
              </Button>

              <Button
                variant="outline"
                size="md"
                onClick={() => navigate('/homepage')}
                iconName="Home"
                iconSize={16}
                fullWidth
              >
                Back to Homepage
              </Button>
            </div>

            <div className="mt-6 p-4 bg-surface-50 rounded-lg">
              <p className="text-sm text-text-secondary font-caption">
                <strong>What's next?</strong><br />
                • Check your email for confirmation<br />
                • Complete any missing documents<br />
                • Start receiving booking requests once approved
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help & Support */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-soft border border-border p-6">
          <div className="flex items-start space-x-4">
            <div className="flex items-center justify-center w-10 h-10 bg-secondary-50 rounded-lg flex-shrink-0">
              <Icon name="HelpCircle" size={20} color="#DAA520" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-text-primary mb-2">
                Need Help?
              </h3>
              <p className="text-text-secondary font-caption mb-4">
                Our support team is here to help you complete your registration successfully.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  iconSize={16}
                >
                  Live Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconSize={16}
                >
                  Email Support
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Phone"
                  iconSize={16}
                >
                  Call (555) 123-4567
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="text-center">
          <p className="text-sm text-text-muted font-caption">
            By registering as a vendor, you're joining a community of trusted South Asian wedding professionals. We're excited to help you grow your business and serve couples planning their special day.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VendorRegistration;